import { createContext, useContext, useMemo } from 'react';
import { createObjectIntentType, getIntents, toIntent, provider } from '../../../intent';
import { onRender } from '../../intentTypes';
import { VERSION } from '../../../version';
import { DEFAULT_PREFIX } from './constants';
import { serialize } from './serialize';
import { replaceTokens } from './replaceTokens';
const TOKEN_VAR_REGEXP = /var\(([-\w]+)\)/g;
function escape(str) {
    // would ideally use `CSS.escape()` here, but that isn't available in all browsers (or JSDOM)
    return str.replace(/\//g, '\\/');
}
function getCustomProperty(prefix, property, token) {
    return `--${prefix}${property}-${escape(token)}`;
}
const TokenContext = createContext({
    tokens: {},
    styles: {}
});
const assignTokensIntent = createObjectIntentType('assignTokens', (decorate)=>(obj)=>decorate(obj));
const processAssignedTokens = onRender((props)=>{
    const inherited = useContext(TokenContext);
    const overrides = getIntents(assignTokensIntent, props);
    const context = useMemo(()=>{
        const tokens = {
            ...inherited.tokens,
            ...overrides
        };
        const styles = serialize({
            hash: '',
            options: {
                prefix: DEFAULT_PREFIX,
                alias: {},
                variants: {},
                layer: '',
                plugins: {},
                tokens: {},
                version: VERSION
            },
            token: {},
            variants: {}
        }, [
            tokens
        ]);
        return {
            tokens,
            styles
        };
    }, [
        inherited,
        overrides
    ]);
    return [
        provider(TokenContext, context),
        context.styles
    ];
});
function forEachToken(values, callback) {
    const categories = Object.keys(values);
    for(let i = 0; i < categories.length; i++){
        const category = categories[i];
        const tokens = Object.keys(values[category]);
        for(let j = 0; j < tokens.length; j++){
            callback(category, tokens[j]);
        }
    }
}
function assignTokens(values, definitions) {
    const result = {};
    forEachToken(values, (category, name)=>{
        var _definitions_category;
        const customProperty = definitions === null || definitions === void 0 ? void 0 : (_definitions_category = definitions[category]) === null || _definitions_category === void 0 ? void 0 : _definitions_category[name];
        if (!customProperty) {
            return;
        }
        const value = values[category][name];
        result[customProperty] = replaceTokens(definitions, value).toString();
    });
    return toIntent([
        assignTokensIntent(result),
        processAssignedTokens
    ]);
}
export function createTokens(input, { prefix = DEFAULT_PREFIX } = {}) {
    const fallbacks = input;
    const definitions = {};
    forEachToken(fallbacks, (category, name)=>{
        if (!definitions[category]) {
            definitions[category] = {};
        }
        definitions[category][name] = getCustomProperty(prefix, category, name);
    });
    const tokens = definitions;
    let reset;
    tokens.assign = (input)=>{
        if (!input) {
            if (!reset) {
                reset = assignTokens(fallbacks, definitions);
            }
            return reset;
        }
        return assignTokens(input, definitions);
    };
    return tokens;
}
export function resolveTokens(tokens, key) {
    const value = tokens[key];
    if (typeof value !== 'string') {
        return value;
    }
    return value.replace(TOKEN_VAR_REGEXP, (_match, customProperty)=>resolveTokens(tokens, customProperty));
}
export function useTokenContext() {
    return useContext(TokenContext);
}
export function useToken(token) {
    const { tokens } = useTokenContext();
    return useMemo(()=>resolveTokens(tokens, token), [
        tokens,
        token
    ]);
}
export function useTokens(tokens) {
    const { tokens: assigned } = useTokenContext();
    return useMemo(()=>tokens.map((token)=>resolveTokens(assigned, token)), [
        assigned,
        tokens
    ]);
}

//# sourceMappingURL=token.js.map